import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../../store/app/appStore';
import type { DeviceMode } from '../../types/external/IApplicationOptions';

/**
 * Detects if the device is a touch/mobile device based on capabilities, not viewport size.
 * This ensures desktop devices with small viewports (e.g., iframes) are still treated as desktop.
 */
const isTouchDevice = (): boolean => {
  // Check for touch capability
  const hasTouchScreen = (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-expect-error - msMaxTouchPoints is IE-specific
    navigator.msMaxTouchPoints > 0
  );

  // Check for mobile user agent patterns
  const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  // Consider it a touch device only if it has touch AND looks like mobile UA
  // This prevents desktop touchscreens from being detected as mobile
  return hasTouchScreen && mobileUserAgent;
};

/**
 * Hook that detects device mode and updates the Zustand store.
 * Uses actual device detection (touch capability + user agent) instead of viewport size
 * to ensure desktop devices with small iframes still get desktop behavior (arrows, etc.)
 *
 * Also listens for changes in touch capabilities (e.g., Chrome DevTools device toggle)
 */
export const useDeviceModeDetection = () => {
  const updateSettings = useAppStore((state) => state.updateSettings);
  const currentMode = useAppStore((state) => state.settings.mode);
  const lastDetectedTouchState = useRef<boolean | null>(null);

  const applyMode = useCallback((mode: DeviceMode) => {
    updateSettings({ mode });

    // Apply CSS classes to root element
    const rootElement = document.getElementById('lobby2-root');
    if (rootElement) {
      rootElement.classList.remove('egt_react_lobby_desktop', 'egt_react_lobby_mobile');
      rootElement.classList.add(`egt_react_lobby_${mode}`);
    }
  }, [updateSettings]);

  const detectAndApplyMode = useCallback(() => {
    const isTouch = isTouchDevice();

    // Only update if touch state actually changed (handles DevTools toggle)
    if (lastDetectedTouchState.current !== isTouch) {
      lastDetectedTouchState.current = isTouch;
      const detectedMode: DeviceMode = isTouch ? 'mobile' : 'desktop';

      if (detectedMode !== currentMode) {
        console.log(`[DeviceModeDetection] Mode changed to ${detectedMode} (touch: ${isTouch})`);
        applyMode(detectedMode);
      }
    }
  }, [currentMode, applyMode]);

  useEffect(() => {
    // Initial detection
    detectAndApplyMode();

    // Listen for pointer/touch capability changes (handles Chrome DevTools device toggle)
    // When DevTools toggles device mode, it changes maxTouchPoints and fires resize
    const handleChange = () => {
      detectAndApplyMode();
    };

    // Use resize as a trigger to re-check touch capabilities
    // This catches Chrome DevTools device emulation toggles
    window.addEventListener('resize', handleChange);

    // Also listen for pointer capability changes via media query
    const touchMediaQuery = window.matchMedia('(pointer: coarse)');
    touchMediaQuery.addEventListener('change', handleChange);

    return () => {
      window.removeEventListener('resize', handleChange);
      touchMediaQuery.removeEventListener('change', handleChange);
    };
  }, [detectAndApplyMode]);

  // Return current mode for components that might need it
  return currentMode;
};